import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RefreshCw, FileText, Clock, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import Layout from '../../components/Layout';
import { issueNoteService } from '../../services/issueNoteService';
import { IssueNoteListDTO, IssueNoteStatus } from '../../types';
import './IssueNotePage.css';

const WORKFLOW_STATUSES: IssueNoteStatus[] = ['PendingApproval', 'Approved', 'Rejected'];

const STATUS_CLASS: Record<IssueNoteStatus, string> = {
  Draft:           'ims-badge isn-badge-draft',
  PendingApproval: 'ims-badge isn-badge-pending',
  Approved:        'ims-badge isn-badge-approved',
  Rejected:        'ims-badge isn-badge-rejected',
  Posted:          'ims-badge isn-badge-posted',
  Cancelled:       'ims-badge isn-badge-cancelled',
};

const STATUS_LABEL: Record<IssueNoteStatus, string> = {
  Draft:           'Draft',
  PendingApproval: 'Pending Approval',
  Approved:        'Approved',
  Rejected:        'Rejected',
  Posted:          'Posted',
  Cancelled:       'Cancelled',
};

const IssueNoteApprovalsPage: React.FC = () => {
  const { t }    = useTranslation();
  const navigate = useNavigate();

  const [items,      setItems]      = useState<IssueNoteListDTO[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error,      setError]      = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res     = await issueNoteService.getAll(1, 500);
      const payload = res.data?.data;
      const all: IssueNoteListDTO[] = (payload as any)?.data ?? (payload as any) ?? [];
      setItems(all.filter(n => WORKFLOW_STATUSES.includes(n.status)));
    } catch {
      setError(t('issueNote.failedToLoad', 'Failed to load issue notes.'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const res     = await issueNoteService.getAll(1, 500);
      const payload = res.data?.data;
      const all: IssueNoteListDTO[] = (payload as any)?.data ?? (payload as any) ?? [];
      setItems(all.filter(n => WORKFLOW_STATUSES.includes(n.status)));
    } catch {
      setError(t('issueNote.failedToLoad', 'Failed to load issue notes.'));
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const pendingCount  = items.filter(n => n.status === 'PendingApproval').length;
  const approvedCount = items.filter(n => n.status === 'Approved').length;
  const rejectedCount = items.filter(n => n.status === 'Rejected').length;

  return (
    <Layout>
      <div className="isn-page">

        {/* ── Header ── */}
        <div className="ims-list-header">
          <h1 className="ims-list-title">{t('issueNote.approvals', 'Issue Note Approvals')}</h1>
          <div className="ims-list-actions">
            <button
              className="btn-refresh"
              onClick={handleRefresh}
              disabled={refreshing}
              title={t('common.refresh', 'Refresh')}
            >
              <RefreshCw size={15} strokeWidth={2} className={refreshing ? 'ims-spinning' : ''} />
              {refreshing ? t('common.refreshing', 'Refreshing…') : t('common.refresh', 'Refresh')}
            </button>
            <button className="ims-btn ims-btn-secondary" onClick={() => navigate('/issue-notes')}>
              {t('issueNote.backToList', 'Back to List')}
            </button>
          </div>
        </div>

        {/* ── Stats row ── */}
        <div className="ims-stats-row">
          <div className="ims-stat-card">
            <div className="ims-stat-icon isn-stat-icon-total">
              <FileText size={18} strokeWidth={2} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{items.length}</span>
              <span className="ims-stat-label">{t('issueNote.inWorkflow', 'In Workflow')}</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon isn-stat-icon-pending">
              <Clock size={18} strokeWidth={2} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{pendingCount}</span>
              <span className="ims-stat-label">{t('issueNote.pendingApproval', 'Pending Approval')}</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon isn-stat-icon-posted">
              <CheckCircle2 size={18} strokeWidth={2} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{approvedCount}</span>
              <span className="ims-stat-label">{t('issueNote.approved', 'Approved')}</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon isn-stat-icon-draft">
              <XCircle size={18} strokeWidth={2} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{rejectedCount}</span>
              <span className="ims-stat-label">{t('issueNote.rejected', 'Rejected')}</span>
            </div>
          </div>
        </div>

        {/* ── Alerts ── */}
        {error && (
          <div className="ims-alert ims-alert-error" style={{ marginBottom: 16 }}>
            <AlertCircle size={16} strokeWidth={2} style={{ flexShrink: 0 }} />
            <span>{error}</span>
            <button type="button" onClick={() => setError(null)} style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit' }}>✕</button>
          </div>
        )}

        {loading && (
          <div className="ims-loading" style={{ padding: '48px 0' }}>
            <div className="spinner-border" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </div>
          </div>
        )}

        {!loading && items.length === 0 && (
          <div style={{ textAlign: 'center', padding: '64px 24px', color: 'var(--ims-text-muted)', background: '#fff', borderRadius: 12, border: '1px solid var(--ims-border)' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--ims-text-main)', marginBottom: 6 }}>
              {t('issueNote.noPendingApprovals', 'No issue notes pending approval')}
            </div>
            <div style={{ fontSize: 13 }}>
              {t('issueNote.noPendingApprovalsDesc', 'All issue notes are in draft, posted, or cancelled state.')}
            </div>
          </div>
        )}

        {!loading && items.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {items.map(item => (
              <div key={item.id} className="ims-card" style={{ margin: 0 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, padding: '16px 20px' }}>
                  <div style={{ flex: 1, minWidth: 240 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                      <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{item.issueNoteNumber}</span>
                      <span className={STATUS_CLASS[item.status] ?? 'ims-badge'}>{STATUS_LABEL[item.status] ?? item.status}</span>
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--ims-text-sub)', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                      {item.warehouseName && (
                        <span><strong>{t('issueNote.warehouse', 'Warehouse')}:</strong> {item.warehouseName}</span>
                      )}
                      <span><strong>{t('issueNote.issueDate', 'Date')}:</strong> {new Date(item.issueDate).toLocaleDateString()}</span>
                      <span><strong>{t('issueNote.items', 'Items')}:</strong> {item.itemCount}</span>
                      {item.issuedByName && (
                        <span><strong>{t('issueNote.issuedBy', 'Issued By')}:</strong> {item.issuedByName}</span>
                      )}
                    </div>

                    {item.status === 'PendingApproval' && (
                      <div style={{ marginTop: 6, fontSize: 12, color: '#92400e', fontWeight: 500 }}>
                        {t('issueNote.awaitingApproval', 'Awaiting approval — open to approve or reject')}
                      </div>
                    )}
                    {item.status === 'Approved' && (
                      <div style={{ marginTop: 6, fontSize: 12, color: '#15803d', fontWeight: 500 }}>
                        {t('issueNote.approvedReadyToPost', 'Approved — ready to post to inventory')}
                      </div>
                    )}
                    {item.status === 'Rejected' && (
                      <div style={{ marginTop: 6, fontSize: 12, color: '#991b1b', fontWeight: 500 }}>
                        {t('issueNote.rejectedResubmit', 'Rejected — issuer must resubmit')}
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                    <button
                      className="ims-btn ims-btn-secondary"
                      style={{ fontSize: 13 }}
                      onClick={() => navigate(`/issue-notes/${item.id}`)}
                    >
                      {item.status === 'Approved' ? t('issueNote.postToInventory', 'Post to Inventory') : t('common.view')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default IssueNoteApprovalsPage;
